import type { CollaboratorPermissionType } from '@/types/database/generated/Collaborator';
import type { MemberPermissionType, MemberStatusType } from '@/types/database/generated/Member';
import type { UserAuthenticationService, UserPlan } from '@/types/database/generated/User';

import { apiClient } from '../http';

export type ResourceType = 'folders' | 'memos' | 'investments' | 'pipelines' | 'dashboards' | 'lists' | 'lifecycles';

export type ResourceCollaboratorUser = {
    id: string;
    firstName?: string;
    lastName?: string;
    emailAddress?: string;
    username?: string | null;
    avatar?: string;
    plan?: UserPlan;
    authenticationService?: UserAuthenticationService;
};

export type ResourceCollaboratorMember = {
    id: string;
    user?: ResourceCollaboratorUser;
    permission?: MemberPermissionType;
    status?: MemberStatusType;
    createdAt?: Date | string;
    updatedAt?: Date | string;
};

export type ResourceCollaborator = {
    id: string;
    member?: ResourceCollaboratorMember;
    permission?: CollaboratorPermissionType;
    resourceId?: string;
    resourceType?: ResourceType;
    role?: CollaboratorPermissionType;
};

export type AddResourceCollaboratorInput = {
    member: string;
    permission: CollaboratorPermissionType;
    status: 'Active';
};

export const collaboratorService = {
    add: (resourceType: ResourceType, resourceId: string, input: AddResourceCollaboratorInput) =>
        resourceType === 'lifecycles'
            ? apiClient.post<
                  ResourceCollaborator,
                  { memberId: string; permission: CollaboratorPermissionType; roles: ['Collaborator'] }
              >(`/api/lifecycles/${resourceId}/collaborators`, {
                  memberId: input.member,
                  permission: input.permission,
                  roles: ['Collaborator'],
              })
            : apiClient.post<ResourceCollaborator, AddResourceCollaboratorInput>(
                  `/api/${resourceType}/${resourceId}/collaborators`,
                  input,
              ),
    delete: (resourceType: ResourceType, resourceId: string, collaboratorId: string) =>
        apiClient.delete<void>(`/api/${resourceType}/${resourceId}/collaborators/${collaboratorId}`),
    list: (resourceType: ResourceType, resourceId: string) =>
        apiClient.get<ResourceCollaborator[]>(`/api/${resourceType}/${resourceId}/collaborators`),
    updatePermission: (
        resourceType: ResourceType,
        resourceId: string,
        collaboratorId: string,
        permission: CollaboratorPermissionType,
    ) =>
        resourceType === 'lifecycles'
            ? apiClient.patch<ResourceCollaborator, { permission: CollaboratorPermissionType }>(
                  `/api/lifecycles/${resourceId}/collaborators/${collaboratorId}`,
                  { permission },
              )
            : apiClient.put<ResourceCollaborator, { permission: CollaboratorPermissionType }>(
                  `/api/${resourceType}/${resourceId}/collaborators/${collaboratorId}/permission`,
                  { permission },
              ),
};
